import React, { useState } from 'react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "General",
      questions: [
        {
          question: "What is ACP 2026?",
          answer: "ACP 2026 (Apostolic Conference Planner) is a comprehensive platform designed to help you discover, plan, and attend Apostolic and Pentecostal conferences across North America. We provide detailed information about events, venues, speakers, travel arrangements, and schedules all in one place."
        },
        {
          question: "Is ACP 2026 free to use?",
          answer: "Yes! ACP 2026 is completely free to use. You can browse events, save your favorites, access schedules, and plan your trips at no cost. Optional premium features may be available for church administrators."
        },
        {
          question: "Which organizations are featured?",
          answer: "We feature conferences from major Pentecostal organizations including UPCI (United Pentecostal Church International), WPF (World Pentecostal Fellowship), ALJC (Assemblies of the Lord Jesus Christ), AFF (Apostolic Faith Fellowship), and other related ministries."
        },
        {
          question: "Do I need an account to use ACP 2026?",
          answer: "No account is required to browse events, view schedules, or access travel information. However, creating a free account allows you to save preferences, and speakers can access their dedicated dashboard to manage their profile and events."
        }
      ]
    },
    {
      category: "Events & Registration",
      questions: [
        {
          question: "How do I register for a conference?",
          answer: "Each event page includes links to the official conference website where you can register. Click 'View Full Details' on any event, then use the 'Visit Website' button to access the official registration page."
        },
        {
          question: "Can I save events I'm interested in?",
          answer: "Yes! Click the star icon on any event card to save it to your favorites. Your saved events are stored locally in your browser and will be available when you return."
        },
        {
          question: "What do the event status badges mean?",
          answer: "Each event displays a status badge: 'Upcoming' (green) for future events, 'Happening Now' (blue, pulsing) for events currently in progress, 'Past Event' (gray) for completed events, 'Pending' (yellow) for events awaiting approval, and 'Cancelled' (red) for cancelled events."
        },
        {
          question: "Can I view past conference information?",
          answer: "Yes! Use the 'Past Events' filter on the Events or Schedule pages to view information about previous conferences, including schedules and speakers."
        }
      ]
    },
    {
      category: "Travel & Accommodation",
      questions: [
        {
          question: "Do you book hotels and flights?",
          answer: "We don't book directly, but we provide convenient links to popular booking platforms like Google Flights, Hotels.com, and Airbnb with dates and locations pre-filled. We also list recommended hotels near each venue."
        },
        {
          question: "How do I find the nearest airport?",
          answer: "Each event page lists the nearest airport code. Click on 'Book Flights' to search for flights to that airport with your preferred dates."
        },
        {
          question: "Are there group rates available?",
          answer: "Group rates vary by hotel and conference. Check the official conference website or contact the host hotel directly to inquire about group discounts."
        },
        {
          question: "What transportation options are available?",
          answer: "Each event page includes links to local transportation options including Uber, Lyft, airport shuttles, and car rentals. Many venues are within walking distance of recommended hotels."
        }
      ]
    },
    {
      category: "Venues & Locations",
      questions: [
        {
          question: "How do I find venue information?",
          answer: "Visit our Venue page to browse all conference locations. Each venue card includes an embedded map, address, nearby airport, and list of events held there."
        },
        {
          question: "Can I get directions to a venue?",
          answer: "Yes! Each venue and event page includes 'Get Directions' buttons that open Google Maps with the venue location pre-loaded."
        },
        {
          question: "Are venues accessible?",
          answer: "Most conference venues are modern convention centers with full accessibility features. For specific accessibility questions, contact the venue directly or check the official conference website."
        }
      ]
    },
    {
      category: "Speakers & Schedule",
      questions: [
        {
          question: "How can I see who's speaking at an event?",
          answer: "Event pages list featured speakers. Click on the Speakers page to browse all speakers and see which events they're speaking at."
        },
        {
          question: "Where can I find the conference schedule?",
          answer: "Detailed schedules are available on each event's detail page. You can also visit the Schedule page to browse all event schedules in one place."
        },
        {
          question: "Can I add events to my calendar?",
          answer: "Yes! Each event detail page includes 'Add to Calendar' buttons for Google Calendar, Outlook, and downloadable .ics files."
        },
        {
          question: "I'm a speaker. How do I manage my profile?",
          answer: "Speakers can log in and access the Speaker Dashboard from the user menu. There you can update your bio, photo, contact information, manage your speaking events, and submit new events for admin approval."
        }
      ]
    },
    {
      category: "Attendee Portal Guide",
      questions: [
        {
          question: "How do I access my attendee dashboard?",
          answer: "After logging in, click on your profile icon in the top right and select 'My Profile' from the dropdown menu. This takes you to your Attendee Dashboard where you can manage your profile, view saved events, track registrations, and see upcoming conferences."
        },
        {
          question: "What can I do from my dashboard?",
          answer: "Your dashboard has several sections: Profile (edit your personal info, photo, church affiliation, dietary/accessibility needs, and interests), Saved Events (events you've bookmarked), Favorite Speakers (speakers you're following), Upcoming Events (conferences happening soon), My Registrations (events you've registered for), and Recommended Events (suggested based on your interests)."
        },
        {
          question: "How do I update my profile information?",
          answer: "Go to your Attendee Dashboard and click the 'Edit Profile' button. You can update your full name, church name, city, state, phone number, profile photo, dietary needs, accessibility needs, and areas of interest. Click 'Save Changes' when you're done. Your interests help us recommend relevant events to you."
        },
        {
          question: "How do I save an event I'm interested in?",
          answer: "Browse the Events page and click the star icon on any event card to save it. Saved events appear in your dashboard under 'Saved Events' so you can easily find them later. You can also save events from the event detail page."
        },
        {
          question: "How do I follow my favorite speakers?",
          answer: "Visit the Speakers page to browse all speakers. Click the heart or follow icon on a speaker's card to add them to your favorites. Your favorite speakers are listed on your dashboard, making it easy to track their upcoming events."
        },
        {
          question: "Where do I find my event tickets?",
          answer: "Click 'My Tickets' in the attendee sidebar menu. This page shows all your event registrations. Use the 'Upcoming' and 'Past' filters to sort your tickets. Each ticket includes event details, date, venue, and a QR code for check-in. You can download or print tickets directly from this page."
        },
        {
          question: "How do I register for an event?",
          answer: "Visit the Events page, find the conference you want to attend, and click 'View Full Details'. On the event detail page, click the 'Register' button. You'll be taken to the registration form where you can fill in your information and complete the registration. Some events are free, while others may require a registration fee."
        },
        {
          question: "Can I add events to my calendar?",
          answer: "Yes! On any event detail page, scroll to the 'Add to Calendar' section. You can add the event to Google Calendar, Outlook, or download a .ics file that works with Apple Calendar and other calendar apps. The calendar entry includes the event location and any notes from the organizer."
        },
        {
          question: "What are recommended events?",
          answer: "Based on the interests you add to your profile, we suggest events that match your preferences. These appear in the 'Recommended Events' section of your dashboard. To get better recommendations, make sure to add your areas of interest in your profile settings."
        },
        {
          question: "How do notification settings work?",
          answer: "In your dashboard, you'll find a Notification Settings panel where you can control which notifications you receive. You can enable or disable event reminders, registration confirmations, and other alerts. Notification settings can be toggled on or off at any time."
        },
        {
          question: "How do I change my password or account settings?",
          answer: "Click 'Settings' in the attendee sidebar menu. From there you can update your password, manage notification preferences, and configure other account settings."
        },
        {
          question: "Can I delete my account?",
          answer: "Yes. In your dashboard settings, there is an option to request account deletion. This will remove your profile and personal data from the platform. If you have active event registrations, you may want to contact the event organizer first."
        }
      ]
    },
    {
      category: "Submitting Events",
      questions: [
        {
          question: "How do I submit an event to be featured?",
          answer: "Visit our Submit Event page and fill out the form with your event details. Our team will review your submission and add it to the platform if it meets our criteria."
        },
        {
          question: "What types of events can be submitted?",
          answer: "We feature conferences, conventions, retreats, youth gatherings, and other significant ministry events from Pentecostal organizations and related ministries."
        },
        {
          question: "How long does it take for an event to be approved?",
          answer: "We typically review and add approved events within 3-5 business days. You'll receive a confirmation email once your event is live on the platform."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "The website isn't working properly. What should I do?",
          answer: "Try clearing your browser cache and refreshing the page (Ctrl+Shift+R on Windows, Cmd+Shift+R on Mac). If issues persist, try using a different browser or contact our support team."
        },
        {
          question: "Can I use ACP 2026 on my mobile device?",
          answer: "Yes! ACP 2026 is fully responsive and works on smartphones, tablets, and desktop computers."
        },
        {
          question: "My saved events disappeared. What happened?",
          answer: "Saved events are stored in your browser's local storage. If you cleared your browser data or switched browsers/devices, your saved events may be lost. We recommend bookmarking important events or adding them to your calendar."
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-xl opacity-90 max-w-3xl">
            Find answers to common questions about using ACP 2026, attending events, and more.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Tip */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> Use Ctrl+F (Cmd+F on Mac) to search for specific keywords on this page.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Categories */}
        {faqs.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{category.category}</h2>
            <div className="space-y-3">
              {category.questions.map((faq, questionIndex) => {
                const index = `${categoryIndex}-${questionIndex}`;
                const isOpen = openIndex === index;
                
                return (
                  <div key={questionIndex} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <button
                      onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                          isOpen ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Still Have Questions */}
        <div className="bg-white rounded-lg shadow-md p-8 mt-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@conferenceplanner2026.com"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Support
            </a>
            <a
              href="/submit-event"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Submit an Event
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
